import { createContext, useContext, useState, useRef } from 'react'
import { MapRef } from 'react-map-gl'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import { v4 as uuidv4 } from 'uuid'
import { Feature, FeatureCollection } from 'geojson'
import { featureCollection } from '@turf/helpers'
import {
  zoomToFeatures,
  createMapboxLayersStyle,
  createDrawMaxBoundsLayerStyle,
} from '@modules/geolocation'
import SelectFeatureMode, {
  drawStyles as selectFeatureDrawStyles,
} from 'mapbox-gl-draw-select-mode'
import DrawRectangle from 'mapbox-gl-draw-rectangle-mode'
import { ProjectMapLayer } from '@core/entities/project/ProjectMapLayer'

export enum PMConfiguratorTab {
  MAP = 0,
  DISCLAIMER = 1,
  LAYERS = 2,
}

export enum PMDrawMode {
  SELECT_FEATURE = 'select_feature',
  SIMPLE_SELECT = 'simple_select',
  DRAW_RECTANGLE = 'draw_rectangle',
}

export enum PMConfiguratorMode {
  LAYER_LIST = 'LAYER_LIST',
  LAYER_FORM = 'LAYER_FORM',
}

export type LayerFormState = {
  id: string | number
  name: string
  visible: boolean
  file: File[]
  fileName: string
  localGeoJson: any
}

export type ProjectMapConfiguratorContext = {
  // UI State
  mode: PMConfiguratorMode
  drawMode: PMDrawMode
  activeTab: PMConfiguratorTab
  selectedFeature: Feature | null
  selectedLayer: ProjectMapLayer | null
  hasLayerGeoJsonChanges: boolean

  // Refs
  mapRef: React.RefObject<MapRef>
  drawRef: React.RefObject<MapboxDraw>

  // UI Actions
  setMode: (mode: PMConfiguratorMode) => void
  setDrawMode: (drawMode: PMDrawMode) => void
  setActiveTab: (tab: PMConfiguratorTab) => void
  setSelectedFeature: (feature: Feature | null) => void
  setSelectedLayer: (layer: ProjectMapLayer | null) => void
  setHasLayerGeoJsonChanges: (hasChanges: boolean) => void

  changeDrawMode: (drawMode: PMDrawMode) => void

  // Layer Actions
  addLayerToMap: (
    layerId: string | number,
    layerFeatureCollection: FeatureCollection,
    visible?: boolean,
  ) => void
  deleteLayerFromMap: (layer: ProjectMapLayer) => void
  selectFeatureOnMap: (featureId: string) => void
  updateFeatureProperties: (
    featureId: string,
    properties: Record<string, any>,
  ) => void
  toggleLayerVisibilityOnMap: (layer: ProjectMapLayer) => void
  zoomToLayerOnMap: (layer: ProjectMapLayer) => void
}

// ================================== Context ==================================

const Context = createContext<ProjectMapConfiguratorContext | null>(null)

export function ProjectMapConfiguratorProvider({
  children,
}: {
  children: React.ReactNode
}) {
  // UI State
  const [mode, setMode] = useState<PMConfiguratorMode>(
    PMConfiguratorMode.LAYER_LIST,
  )
  const [drawMode, setDrawMode] = useState<PMDrawMode>(
    PMDrawMode.SELECT_FEATURE,
  )
  const [activeTab, setActiveTab] = useState<PMConfiguratorTab>(
    PMConfiguratorTab.MAP,
  )
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null)
  const [selectedLayer, setSelectedLayer] = useState<ProjectMapLayer | null>(
    null,
  )
  const [hasLayerGeoJsonChanges, setHasLayerGeoJsonChanges] = useState(false)

  // Refs
  const mapRef = useRef<MapRef>(null)
  const drawRef = useRef<MapboxDraw>(
    new MapboxDraw({
      userProperties: true,
      displayControlsDefault: false,
      modes: {
        ...SelectFeatureMode({
          ...MapboxDraw.modes,
          draw_rectangle: DrawRectangle,
        }),
      },
      styles: [
        ...createDrawMaxBoundsLayerStyle({ id: 'max-bounds-polygon' }),
        ...selectFeatureDrawStyles(
          createMapboxLayersStyle({ id: 'layer-style' }),
        ),
      ],
    }),
  )

  const changeDrawMode = (drawMode: PMDrawMode) => {
    if (!drawRef.current) return

    setDrawMode(drawMode)

    // Type assertion since we know these are valid modes supported by MapboxDraw
    drawRef.current?.changeMode(drawMode as string)
  }

  // Layer Actions
  const addLayerToMap = (
    layerId: string | number,
    layerFeatureCollection: FeatureCollection,
    visible: boolean = true,
  ) => {
    if (!drawRef.current) return

    layerFeatureCollection.features.forEach((feature) => {
      feature.properties.id = uuidv4()
      feature.properties.layerId = layerId
      feature.properties.visible = visible
      feature.properties.hoverOpacity = feature?.properties?.hoverOpacity ?? 1.0
    })

    drawRef.current.add(layerFeatureCollection)
  }

  const deleteLayerFromMap = (layer: ProjectMapLayer) => {
    if (!drawRef.current) return

    const affectedFeatures = drawRef.current
      .getAll()
      .features.filter((feature) => feature.properties.layerId === layer.id)

    drawRef.current.delete(
      affectedFeatures.map((feature) => feature?.id?.toString()),
    )
  }

  const selectFeatureOnMap = (featureId: string) => {
    if (!drawRef.current) return

    const feature = drawRef.current?.get(featureId)

    setSelectedFeature(feature)
  }

  const updateFeatureProperties = (
    featureId: string,
    properties: Record<string, any>,
  ) => {
    if (!drawRef.current) return

    Object.entries(properties).forEach(([key, value]) => {
      drawRef.current?.setFeatureProperty(featureId, key, value)
    })

    setHasLayerGeoJsonChanges(true)
  }

  const toggleLayerVisibilityOnMap = (layer: ProjectMapLayer) => {
    if (!drawRef.current) return

    const affectedFeatures = drawRef.current
      .getAll()
      .features.filter((feature) => feature.properties.layerId === layer.id)

    affectedFeatures.forEach((feature) => {
      feature.properties.visible = !layer.visible
    })

    const updateMapWithFeatureCollection = featureCollection(affectedFeatures)

    drawRef.current.add(updateMapWithFeatureCollection)
  }

  const zoomToLayerOnMap = (layer: ProjectMapLayer) => {
    if (!drawRef.current) return

    const features = drawRef.current
      .getAll()
      .features.filter((feature) => feature.properties.layerId === layer.id)

    zoomToFeatures({ mapRef, features })
  }

  return (
    <Context.Provider
      value={{
        // State
        mode,
        drawMode,
        activeTab,
        selectedFeature,
        selectedLayer,
        hasLayerGeoJsonChanges,
        // Refs
        mapRef,
        drawRef,

        // UI Actions
        setMode,
        setDrawMode,
        setActiveTab,
        setSelectedFeature,
        setSelectedLayer,
        setHasLayerGeoJsonChanges,

        changeDrawMode,

        // Layer Actions
        addLayerToMap,
        deleteLayerFromMap,
        selectFeatureOnMap,
        updateFeatureProperties,
        toggleLayerVisibilityOnMap,
        zoomToLayerOnMap,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export function useProjectMapConfigurator() {
  const context = useContext(Context)
  if (!context) {
    throw new Error(
      'useProjectMapConfigurator must be used within ProjectMapConfiguratorProvider',
    )
  }
  return context
}
